// @flow
import { CanonicalTileID } from "./tile_id";
import EXTENT from "../data/extent";
import { RasterBoundsArray } from "../data/array_types";
import { ResourceType, getImage } from "../util/ajax";
import ImageSource from "./image_source";
import { Event, ErrorEvent, Evented } from "../util/evented";
import LngLat from "../geo/lng_lat";
import MercatorCoordinate from "../geo/mercator_coordinate";
import rasterBoundsAttributes from "../data/raster_bounds_attributes";
import SegmentVector from "../data/segment";
import Texture from "../render/texture";
import { extend } from "../util/util";

type Coordinates = [
    [number, number],
    [number, number],
    [number, number],
    [number, number]
];

/**
 * A data source containing an image.
 * (See the [Style Specification](https://www.mapbox.com/mapbox-gl-style-spec/#sources-image) for detailed documentation of options.)
 *
 * @example
 * // add to map
 * map.addSource('some id', {
 *    type: 'image-map',
 *    url: 'http://map.geoq.cn/ArcGIS/rest/services/ChinaOnlineCommunity/MapServer',
 *    rebaseRequestUrl:function(url,params){
 * // 根据不同服务构建url，参考组件层实现
 *   }
 * });
 *
 * // update coordinates
 * var mySource = map.getSource('some id');
 * mySource.setCoordinates([
 *     [-76.54335737228394, 39.18579907229748],
 *     [-76.52803659439087, 39.1838364847587],
 *     [-76.5295386314392, 39.17683392507606],
 *     [-76.54520273208618, 39.17876344106642]
 * ]);
 *
 * // update url and coordinates simultaneously
 * mySource.updateImage({
 *    url: 'https://www.mapbox.com/images/bar.png',
 *    coordinates: [
 *        [-76.54335737228394, 39.18579907229748],
 *        [-76.52803659439087, 39.1838364847587],
 *        [-76.5295386314392, 39.17683392507606],
 *        [-76.54520273208618, 39.17876344106642]
 *    ]
 * })
 *
 * map.removeSource('some id');  // remove
 * @see [Add an image](https://www.mapbox.com/mapbox-gl-js/example/image-on-a-map/)
 */
class ImageMapSource extends ImageSource {
    /**
     * @private
     */
    constructor(
        id: string,
        options:
            | ImageSourceSpecification
            | VideoSourceSpecification
            | CanvasSourceSpecification,
        dispatcher: Dispatcher,
        eventedParent: Evented
    ) {
        super(id, options, dispatcher, eventedParent);
        // 构建图片url方法
        this.rebaseRequestUrl =
            options.rebaseRequestUrl ||
            function (url) {
                return url;
            };
        // 方位点
        this.coordinates = [];
        // 设置source类型，按范围覆盖瓦片
        this.type = "image-map";
        // mapbox-gl-修改说明：新增crs属性
        // 修改人：龚跃健 2024年6月17日
        this.crs = eventedParent.crs;
    }

    getCoordinates() {
        return this.coordinates;
    }

    onAdd(map) {
        this.map = map;
        map.on("resize", this.innerResize.bind(this));
        map.on("rotateend", this.innnerRotateEnd.bind(this));
        map.on("pitchend", this.innnerPitchEnd.bind(this));
        map.on("moveend", this.innerMoveEnd.bind(this));
        // 初始化裁剪区
        const options = this._options || this.options
        if(options){
            this.setClippingArea(options.clippingArea)
        }
        this.load();
    }

    innerResize() {
        this.loadImage();
    }
    innnerRotateEnd() {
        this.loadImage();
    }
    innnerPitchEnd() {
        this.loadImage();
    }
    innerMoveEnd() {
        this.loadImage();
    }
    loadImage() {
        this.load();
    }

    load(newCoordinates?: Coordinates, successCallback?: () => void) {
        this._loaded = false;
        this.fire(new Event("dataloading", { dataType: "source" }));
        this.url = this.options.url;
        const boxs = newCoordinates || this.transfromMapCoords();
        const wh = this.map._containerDimensions();
        const requestParams = (this.options && this.options.requestParams) ? this.options.requestParams : {}
        // mapbox-gl-修改说明：image_map_source支持post请求资源
        // 修改人：杨婷茹 2024年5月21日
        // post请求时，解析已被拼接在tileUrl上的bbox和size值
        let tileUrl
        if (requestParams && requestParams.method === 'POST') {    
            // 解析出bbx和size的值
            requestParams.data.size = `${wh[0]},${wh[1]}`;
            const isReverse = this.url.indexOf("reversebbox=true") >= 0 ? true : false;
            requestParams.data.bbox = this._getBbox(boxs,isReverse)
            tileUrl = this.url
        } else {
            tileUrl = this.applyUrl(this.url, boxs, wh[0], wh[1]);
        }
        getImage(
            this.map._requestManager.transformRequest(
                tileUrl,
                ResourceType.Image,
                requestParams
            ),
            (err, image) => {
                this._loaded = true;
                if (err) {
                    this.fire(new ErrorEvent(err));
                } else if (image) {
                    this.image = image;
                    if (boxs) {
                        this.coordinates = boxs;
                    }
                    if (successCallback) {
                        successCallback();
                    }
                    this._finishLoading();
                }
            }
        );
    }

    loadTile(tile: Tile, callback: Callback<void>) {
        // We have a single tile -- whoose coordinates are this.tileID -- that
        // covers the image we want to render.  If that's the one being
        // requested, set it up with the image; otherwise, mark the tile as
        // `errored` to indicate that we have no data for it.
        // If the world wraps, we may have multiple "wrapped" copies of the
        // single tile.
        if (this.tileID && this.tileID.equals(tile.tileID.canonical)) {
            this.tiles[String(tile.tileID.wrap)] = tile;
            // 设置在parentTile时，不发生瓦片渲染的渐变
            // 此处在底层program.js draw函数中被处理，原理修改顶点透明度
            tile.refreshedUponExpiration = true;
            tile.buckets = {};
            callback(null);
        } else {
            tile.state = "errored";
            callback(null);
        }
    }

    transfromMapCoords() {
        const bounds = this.map.getBounds();
        // toArray= [[-73.9876, 40.7661], [-73.9397, 40.8002]]
        const arr = bounds.toArray();
        return [
            [arr[0][0], arr[1][1]],
            [arr[1][0], arr[1][1]],
            [arr[1][0], arr[0][1]],
            [arr[0][0], arr[0][1]],
        ];
    }

    transfromCoordsToBound(newCoordinates) {
        const bx = [],
            by = [];
        newCoordinates.forEach((coord) => {
            bx.push(coord[0]);
            by.push(coord[1]);
        });
        return [
            Math.min(...bx),
            Math.min(...by),
            Math.max(...bx),
            Math.max(...by),
        ];
    }
    // 获取bbox，该方法逻辑抽取自applyUrl()
    _getBbox(coordinates, isReverse) {
        const bound = this.transfromCoordsToBound(coordinates);

        if (isReverse) {
            const [x1, y1, x2, y2] = bound;
            bound[0] = y1;
            bound[1] = x1;
            bound[2] = y2;
            bound[3] = x2;
        }
        return bound.toString()
    }
    // given a list of urls, choose a url template and return a tile URL
    applyUrl(url, newCoordinates?: Coordinates, imageWidth, imageHeight) {
        const bound = this.transfromCoordsToBound(newCoordinates);

        const x1 = this._project(new LngLat(bound[0], bound[1]));
        const x2 = this._project(new LngLat(bound[2], bound[3]));

        const mercatorBound = [x1.x, x1.y, x2.x, x2.y];

        const reverse = url.indexOf("reversebbox=true") >= 0;
        if (reverse) {
            [bound, mercatorBound].forEach((b) => {
                const [x1, y1, x2, y2] = b;
                b[0] = y1;
                b[1] = x1;
                b[2] = y2;
                b[3] = x2;
            });
        }

        return this.rebaseRequestUrl(url, {
            latlngBounds: bound,
            mercatorBound,
            imageSize: [imageWidth, imageHeight],
        });
    }

    _project(latlng) {
        const mercator = {};
        const earthRad = 6378137.0;
        // console.log("mercator-poi",poi);
        mercator.x = ((latlng.lng * Math.PI) / 180) * earthRad;
        const a = (latlng.lat * Math.PI) / 180;
        mercator.y =
            (earthRad / 2) *
            Math.log((1.0 + Math.sin(a)) / (1.0 - Math.sin(a)));
        // console.log("mercator",mercator);
        return mercator; //[12727039.383734727, 3579066.6894065146
    }

    _finishLoading() {
        if (this.map) {
            this.setCoordinates(this.coordinates);
            this.fire(
                new Event("data", {
                    dataType: "source",
                    sourceDataType: "metadata",
                })
            );
        }
    }

    updateImage(options: { url: string, coordinates?: Coordinates }) {
        if (!this.image || !options.url) {
            return this;
        }
        this.options.url = options.url;
        this.load(this.coordinates, () => {
            this.texture = null;
        });
        return this;
    }

    /**
     * Sets the image's coordinates and re-renders the map.
     *
     * @param {Array<Array<number>>} coordinates Four geographical coordinates,
     *   represented as arrays of longitude and latitude numbers, which define the corners of the image.
     *   The coordinates start at the top left corner of the image and proceed in clockwise order.
     *   They do not have to represent a rectangle.
     * @returns {ImageSource} this
     */
    setCoordinates(coordinates: Coordinates) {
        this.coordinates = coordinates;

        // Calculate which mercator tile is suitable for rendering the video in
        // and create a buffer with the corner coordinates. These coordinates
        // may be outside the tile, because raster tiles aren't clipped when rendering.

        // transform the geo coordinates into (zoom 0) tile space coordinates
        // mapbox-gl-修改说明：传入crs
        // 修改人：龚跃健 2024年6月17日
        const { crs } = this;
        const cornerCoords = coordinates.map((item) => {
            return MercatorCoordinate.fromLngLat(item, null, crs);
        });

        // Compute the coordinates of the tile we'll use to hold this image's
        // render data
        this.tileID = getCoordinatesCenterTileID(cornerCoords);

        // Constrain min/max zoom to our tile's zoom level in order to force
        // SourceCache to request this tile (no matter what the map's zoom
        // level)
        this.minzoom = this.maxzoom = this.tileID.z;

        // Transform the corner coordinates into the coordinate space of our
        // tile.
        const tileCoords = cornerCoords.map((coord) =>
            this.tileID.getTilePoint(coord)._round()
        );

        this._boundsArray = new RasterBoundsArray();
        this._boundsArray.emplaceBack(tileCoords[0].x, tileCoords[0].y, 0, 0);
        this._boundsArray.emplaceBack(
            tileCoords[1].x,
            tileCoords[1].y,
            EXTENT,
            0
        );
        this._boundsArray.emplaceBack(
            tileCoords[3].x,
            tileCoords[3].y,
            0,
            EXTENT
        );
        this._boundsArray.emplaceBack(
            tileCoords[2].x,
            tileCoords[2].y,
            EXTENT,
            EXTENT
        );

        if (this.boundsBuffer) {
            this.boundsBuffer.destroy();
            delete this.boundsBuffer;
        }

        this.fire(
            new Event("data", { dataType: "source", sourceDataType: "content" })
        );
        return this;
    }

    prepare() {
        if (Object.keys(this.tiles).length === 0 || !this.image) {
            return;
        }
        const context = this.map.painter.context;
        const gl = context.gl;

        if (!this.boundsBuffer) {
            this.boundsBuffer = context.createVertexBuffer(
                this._boundsArray,
                rasterBoundsAttributes.members
            );
        }

        if (!this.boundsSegments) {
            this.boundsSegments = SegmentVector.simpleSegment(0, 0, 4, 2);
        }

        if (!this.texture) {
            this.texture = new Texture(context, this.image, gl.RGBA);
            this.texture.bind(gl.LINEAR, gl.CLAMP_TO_EDGE);
        }
        // 更新图元
        this.texture.update(this.image);

        for (const w in this.tiles) {
            const tile = this.tiles[w];
            if (tile.state !== "loaded") {
                tile.state = "loaded";
                tile.texture = this.texture;
            }
        }
    }
    // mapbox-gl-修改说明：解决source序列化后再次加载时类型错误问题，以及coordinates为空数组导致的类型验证报错问题
    // 修改人：赵凯 2024年9月24日
    serialize(): Object {
        return extend({}, this.options, {
            type: this.type,
            coordinates: this.transfromMapCoords(),
        });
    }
}

/**
 * Given a list of coordinates, get their center as a coordinate.
 *
 * @returns centerpoint
 * @private
 */
export function getCoordinatesCenterTileID(coords: Array<MercatorCoordinate>) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const coord of coords) {
        minX = Math.min(minX, coord.x);
        minY = Math.min(minY, coord.y);
        maxX = Math.max(maxX, coord.x);
        maxY = Math.max(maxY, coord.y);
    }

    const dx = maxX - minX;
    const dy = maxY - minY;
    const dMax = Math.max(dx, dy);
    const zoom = Math.max(0, Math.floor(-Math.log(dMax) / Math.LN2));
    const tilesAtZoom = Math.pow(2, zoom);

    return new CanonicalTileID(
        zoom,
        Math.floor(((minX + maxX) / 2) * tilesAtZoom),
        Math.floor(((minY + maxY) / 2) * tilesAtZoom)
    );
}
export default ImageMapSource;
